import React, { useEffect, useMemo, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MuiTooltip from "@mui/material/Tooltip";
import {
  apiCallGetRepeatVisitorAnalytics,
  apiCallGetVenueUserIntentAnalytics,
  apiCallGetEventVsNormalNight,
  apiCallGetVenueTopKeyWords
} from "../../../Core/Apicall";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

type FilterType = "last7days" | "last30days" | "last90days";

const FILTERS = [
  { key: "last7days", label: "Last 7 Days" },
  { key: "last30days", label: "Last 30 Days" },
  { key: "last90days", label: "Last 90 Days" },
];

export default function CustomerBehavior({ venueId }: { venueId: string }) {
  const [filter, setFilter] = useState<FilterType>("last90days");
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);
  const [intentAnalytics, setIntentAnalytics] = useState<any[]>([]);
  const [topKeywords, setTopKeywords] = useState<any[]>([]);
  const [eventAnalytics, setEventAnalytics] = useState<any>(null);

  const fetchEventVsNormalNight = async () => {
    try {
      let params = {
        venueId,
        filter,
      }

      let res = await apiCallGetEventVsNormalNight(params);
      setEventAnalytics(res?.data);

    } catch (err: any) {
      console.log("Error message:", err);
    }
  };
  const eventVsNormalChart = useMemo(() => {
    if (!eventAnalytics) return null;

    return {
      labels: ["Event Nights", "Normal Nights"],
      datasets: [
        {
          label: "Check-in %",
          data: [
            parseFloat(eventAnalytics.eventNightPercentage.replace("%", "")),
            parseFloat(eventAnalytics.normalNightPercentage.replace("%", "")),
          ],
          backgroundColor: ["#8B5CF6", "#3B82F6"],
          borderRadius: 8,
          barThickness: 60,
        },
      ],
    };
  }, [eventAnalytics]);

  const eventVsNormalDonut = useMemo(() => {
    if (!eventAnalytics) return null;

    return {
      labels: ["Event Nights", "Normal Nights"],
      datasets: [
        {
          data: [
            eventAnalytics.eventNightCheckIns,
            eventAnalytics.normalNightCheckIns,
          ],
          backgroundColor: ["#8B5CF6", "#E5E7EB"],
          borderWidth: 0,
          cutout: "70%",
        },
      ],
    };
  }, [eventAnalytics]);

  const totalCheckIns =
    (eventAnalytics?.eventNightCheckIns || 0) +
    (eventAnalytics?.normalNightCheckIns || 0);

  /* ================= FETCH RETENTION ================= */
  const fetchRetentionAnalytics = async () => {
    try {
      setLoading(true);

      const res = await apiCallGetRepeatVisitorAnalytics({
        venueId,
        filter,
      });

      if (res?.status) {
        setAnalytics(res.data);
      }
    } catch (err) {
      console.error("Retention API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= FETCH INTENT ================= */
  const fetchIntentAnalytics = async () => {
    try {
      const res = await apiCallGetVenueUserIntentAnalytics({
        venueId,
        filter,
      });

      if (res?.status) {
        setIntentAnalytics(res.data || []);
      }
    } catch (err) {
      console.error("Intent API Error:", err);
    }
  };

  /* ================= FETCH Top Keywords ================= */
  const fetchTopKeywords = async () => {
    try {
      const res = await apiCallGetVenueTopKeyWords({
        venueId,
        filter,
      });

      if (res?.status) {
        setTopKeywords(res.data || []);
      }
    } catch (err) {
      console.error("Top Keywords API Error:", err);
    }
  };

  useEffect(() => {
    fetchRetentionAnalytics();
    fetchIntentAnalytics();
    fetchEventVsNormalNight();
    fetchTopKeywords();
  }, [filter]);

  /* ================= Doughnut Chart ================= */
  const chartData = useMemo(() => {
    if (!analytics) return null;

    return {
      labels: ["Repeat Visitors", "One-Time Visitors"],
      datasets: [
        {
          data: [
            analytics.repeatUsers,
            analytics.totalUniqueUsers - analytics.repeatUsers,
          ],
          backgroundColor: ["#10B981", "#E5E7EB"],
          borderWidth: 0,
          cutout: "70%",
        },
      ],
    };
  }, [analytics]);

  /* ================= Intent Calculations ================= */

  // Total responses for all intents
  const totalIntentResponses = useMemo(() => {
    return intentAnalytics.reduce((acc, curr) => acc + curr.total, 0);
  }, [intentAnalytics]);

  // Sort highest intent first
  const sortedIntents = useMemo(() => {
    return [...intentAnalytics].sort((a, b) => b.total - a.total);
  }, [intentAnalytics]);

  /* ================= Top Keywords Chart ================= */
  const keywordChartData = useMemo(() => {
    if (!topKeywords || topKeywords.length === 0) return null;

    return {
      labels: topKeywords.map((k: any) => k.intent),
      datasets: [
        {
          label: "Positive %",
          data: topKeywords.map((k: any) => k.positivePercentage),
          backgroundColor: "#10B981",
          borderRadius: 8,
          barThickness: 40,
        },
        {
          label: "Negative %",
          data: topKeywords.map((k: any) => k.negativePercentage),
          backgroundColor: "#EF4444",
          borderRadius: 8,
          barThickness: 40,
        },
      ],
    };
  }, [topKeywords]);

  const columnChartData = useMemo(() => {
    return {
      labels: sortedIntents.map((i) => i.intent),
      datasets: [
        {
          label: "Positive %",
          data: sortedIntents.map((i) => i.positivePercentage),
          backgroundColor: "#10B981",
          borderRadius: 8,
          barThickness: 40,
        },
        {
          label: "Negative %",
          data: sortedIntents.map((i) => i.negativePercentage),
          backgroundColor: "#EF4444",
          borderRadius: 8,
          barThickness: 40,
        },
      ],
    };
  }, [sortedIntents]);



  return (
    <div style={pageWrapper}>
      {/* FILTERS */}
      <div style={filterWrapper}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key as FilterType)}
            style={{
              ...filterButton,
              ...(filter === f.key ? activeFilter : {}),
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ================= CUSTOMER RETENTION ================= */}
      <div style={mainCard}>
        <div style={cardHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h3 style={{ margin: 0 }}>Customer Retention</h3>
            <MuiTooltip
              title="Track how many users return within selected time range."
              arrow
            >
              <InfoOutlinedIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
            </MuiTooltip>
          </div>
        </div>

        {loading ? (
          <p>Loading analytics...</p>
        ) : !analytics ? (
          <p>No data found.</p>
        ) : (
          <div style={{ display: "flex", gap: 50, alignItems: "center" }}>
            {/* Doughnut */}
            <div style={{ width: 220, height: 220, position: "relative" }}>
              {chartData && (
                <Doughnut
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                  }}
                />
              )}

              <div style={donutCenter}>
                <h1 style={{ margin: 0, fontSize: 34, fontWeight: 800 }}>
                  {analytics.repeatVisitorRate}
                </h1>
                <p style={{ margin: 0, color: "#6B7280" }}>
                  Repeat Rate
                </p>
              </div>
            </div>

            {/* KPI Cards */}
            <div style={{ flex: 1 }}>
              <StatCard
                title="Total Unique Visitors"
                value={analytics.totalUniqueUsers}
                color="#3B82F6"
              />
              <StatCard
                title="Repeat Visitors"
                value={analytics.repeatUsers}
                color="#10B981"
              />
            </div>
          </div>
        )}
      </div>

      {/* ================= WHY USERS CHOOSE ================= */}
      <div style={reasonCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ marginBottom: 5 }}>
            Why Users Choose This Venue
          </h3>
          <MuiTooltip
            title="Top reasons customers choose this venue based on feedback."
            arrow
          >
            <InfoOutlinedIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
          </MuiTooltip>
        </div>

        {sortedIntents.length === 0 ? (
          <p style={{ color: "#6B7280" }}>
            No intent data available.
          </p>
        ) : (
          <div style={{ marginTop: 30 }}>
            <div style={{ height: 400 }}>
              <Bar
                data={columnChartData}
                options={columnChartOptions}
              />
            </div>
          </div>
        )}
      </div>

      {/* ================= EVENT VS NORMAL NIGHT ================= */}
      <div style={mainCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0 }}>Event vs Normal Nights</h3>

          <MuiTooltip
            title="Compare visitor check-ins during event nights versus normal nights."
            arrow
          >
            <InfoOutlinedIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
          </MuiTooltip>
        </div>

        {!eventAnalytics ? (
          <p style={{ marginTop: 20 }}>No data available.</p>
        ) : (
          <div
            style={{
              display: "flex",
              gap: 60,
              alignItems: "center",
              marginTop: 30,
            }}
          >
            {/* Doughnut */}
            <div style={{ width: 240, height: 240, position: "relative" }}>
              {eventVsNormalDonut && (
                <Doughnut
                  data={eventVsNormalDonut}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                  }}
                />
              )}

              {/* CENTER VALUE */}
              <div style={donutCenter}>
                <h1 style={{ margin: 0, fontSize: 34, fontWeight: 800 }}>
                  {totalCheckIns}
                </h1>
                <p style={{ margin: 0, color: "#6B7280" }}>
                  Total Check-ins
                </p>
              </div>
            </div>

            {/* Bar Chart */}
            <div style={{ height: 350, flex: 1 }}>
              {eventVsNormalChart && (
                <Bar
                  data={eventVsNormalChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `Check-in : ${Math.round(context.raw)}%`;
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: {
                          callback: function (value: any) {
                            return value + "%";
                          },
                        },
                      },
                    },
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* ================= Top Keywords ================= */}
      <div style={reasonCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ marginBottom: 5 }}>Top Keywords</h3>

          <MuiTooltip
            title="Top keywords associated with this venue based on customer feedback."
            arrow
          >
            <InfoOutlinedIcon style={{ fontSize: 18, color: "#9CA3AF" }} />
          </MuiTooltip>
        </div>

        {!topKeywords || topKeywords.length === 0 ? (
          <p style={{ color: "#6B7280" }}>No keyword data available.</p>
        ) : (
          <div style={{ marginTop: 30 }}>
            <div style={{ height: 400 }}>
              {keywordChartData && (
                <Bar
                  data={keywordChartData}
                  options={columnChartOptions}
                />
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

/* ================= Stat Card ================= */
function StatCard({
  title,
  value,
  color,
}: {
  title: string;
  value: string | number;
  color: string;
}) {
  return (
    <div
      style={{
        padding: 20,
        marginBottom: 20,
        borderRadius: 16,
        background: "#ffffff",
        boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
        borderLeft: `5px solid ${color}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 13, color: "#6B7280" }}>
        {title}
      </p>
      <h2
        style={{
          margin: "6px 0 0",
          fontSize: 28,
          fontWeight: 800,
          color: "#111827",
        }}
      >
        {value}
      </h2>
    </div>
  );
}

/* ================= Styles ================= */

const pageWrapper: React.CSSProperties = {
  padding: 40,
  background: "#F3F4F6",
  minHeight: "100vh",
};

const filterWrapper: React.CSSProperties = {
  display: "flex",
  gap: 12,
  marginTop: 25,
  justifyContent: "flex-end",
};

const filterButton: React.CSSProperties = {
  padding: "8px 18px",
  borderRadius: 25,
  border: "1px solid #E5E7EB",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
};

const activeFilter: React.CSSProperties = {
  background: "linear-gradient(135deg, #3B82F6, #2563EB)",
  color: "#fff",
  border: "none",
};

const mainCard: React.CSSProperties = {
  marginTop: 30,
  background: "#ffffff",
  borderRadius: 20,
  padding: 35,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
};

const reasonCard: React.CSSProperties = {
  marginTop: 30,
  background: "#ffffff",
  borderRadius: 20,
  padding: 35,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
};

const donutCenter: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  textAlign: "center",
};

const progressBarBg: React.CSSProperties = {
  width: "100%",
  height: 10,
  background: "#E5E7EB",
  borderRadius: 20,
  marginTop: 8,
};

const progressBarFill: React.CSSProperties = {
  height: "100%",
  background: "linear-gradient(90deg, #3B82F6, #2563EB)",
  borderRadius: 20,
};

const cardHeader: React.CSSProperties = {
  marginBottom: 20,
};


const intentCard: React.CSSProperties = {
  background: "#F9FAFB",
  borderRadius: 18,
  padding: 20,
  marginBottom: 20,
  boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
};

const intentHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const percentageBadge: React.CSSProperties = {
  background: "linear-gradient(135deg, #3B82F6, #2563EB)",
  color: "#fff",
  padding: "4px 12px",
  borderRadius: 20,
  fontWeight: 700,
  fontSize: 12,
};

const intentBarBg: React.CSSProperties = {
  height: 8,
  background: "#E5E7EB",
  borderRadius: 20,
  marginTop: 12,
};

const intentBarFill: React.CSSProperties = {
  height: "100%",
  borderRadius: 20,
  background: "linear-gradient(90deg, #3B82F6, #2563EB)",
  transition: "width 0.5s ease",
};

const sentimentBar: React.CSSProperties = {
  display: "flex",
  height: 10,
  borderRadius: 20,
  overflow: "hidden",
  marginTop: 10,
};

const sentimentLabelRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  marginTop: 6,
  fontSize: 14,
};

const columnChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 800,
    easing: "easeOutQuart" as const,
  },
  plugins: {
    legend: {
      position: "top" as const,
      labels: {
        usePointStyle: true,
        padding: 20,
        font: {
          weight: "bold" as const,
        },
      },
    },
    tooltip: {
      backgroundColor: "#111827",
      titleColor: "#fff",
      bodyColor: "#fff",
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        label: function (context: any) {
          return `${context.dataset.label}: ${context.raw}%`;
        },
      },
    },
  },
  scales: {
    x: {
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        font: {
          weight: "bold" as const,
        },
      },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      max: 100,
      ticks: {
        callback: function (value: any) {
          return value + "%";
        },
      },
      grid: {
        color: "#E5E7EB",
      },
    },
  },
};


